import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOptimizedQueryKey } from "@/hooks/useOptimizedQueryKey";
import { useAuthReady } from "@/hooks/useAuthReady";

export interface FormTemplate {
  id: string;
  title: string;
  description: string;
  file_name: string;
  file_path: string;
  is_active: boolean;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  slug: string | null;
}

export async function fetchFormTemplates(organizationId: string): Promise<FormTemplate[]> {
  // SECURITY: Query with RLS policies enforcing org membership
  // Selects only required columns for data minimization
  const { data, error } = await supabase
    .from("form_templates")
    .select("id, title, description, file_name, file_path, is_active, is_public, created_at, updated_at, slug")
    .eq("organization_id", organizationId)
    .eq("is_active", true)
    .order("created_at", { ascending: false });

  if (error) {
    console.error('[fetchFormTemplates] Query error:', error);
    throw error;
  }

  return (data ?? []) as unknown as FormTemplate[];
}

export function useFormTemplatesQuery() {
  const { organizationId } = useOptimizedQueryKey();
  const { isReady, hasAccessToken } = useAuthReady();

  const queryKey = useMemo(
    () => ["org-templates", organizationId ?? "unauthenticated"] as const,
    [organizationId]
  );

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      // CRITICAL FIX: Don't throw error, return empty array when no org context
      // This allows graceful degradation instead of breaking the UI
      if (!organizationId) {
        if (import.meta.env.DEV) {
          console.debug('[useFormTemplatesQuery] No organization context - returning empty array');
        }
        return [];
      }

      return fetchFormTemplates(organizationId);
    },
    enabled: isReady && hasAccessToken && !!organizationId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });

  return {
    ...query,
    queryKey,
  };
}
